import axios from "../API/axios";

const inFlight = new Map();
const cache = new Map();

const TTL = {
  USER_CONFIG: 60_000,
  NOTIFICATION_COUNT: 15_000,
  MESSAGE_COUNT: 15_000,
  OWN_COMPANIES: 60_000,
  EMOJI_DDL: 300_000,
  CONNECTION_LIST: 30_000,
  CHECK_CONNECTION: 30_000,
  SHORT_MUTUAL: 120_000,
  RECOMMENDATION_LIST: 60_000,
  SYNC_ACCOUNT: 120_000,
  OWN_INFO: 60_000,
  PROFILE_PERCENTAGE: 60_000,
};

const getLangKey = () => {
  const language = localStorage.getItem("Lang");
  return language === "en" ? "" : language;
};

const getSessionId = () => localStorage.getItem("sessionId");

const buildCacheKey = (endpoint, params = {}) =>
  `${endpoint}:${JSON.stringify(params)}`;

const invalidateByPrefix = (prefix) => {
  for (const key of [...cache.keys(), ...inFlight.keys()]) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
      inFlight.delete(key);
    }
  }
};

const fetchWithDedup = async (
  cacheKey,
  fetcher,
  { ttl = 60_000, forceRefresh = false } = {}
) => {
  if (!forceRefresh) {
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < ttl) {
      return cached.data;
    }
    if (inFlight.has(cacheKey)) {
      return inFlight.get(cacheKey);
    }
  } else {
    cache.delete(cacheKey);
    inFlight.delete(cacheKey);
  }

  const promise = fetcher()
    .then((data) => {
      cache.set(cacheKey, { data, timestamp: Date.now() });
      return data;
    })
    .finally(() => {
      inFlight.delete(cacheKey);
    });

  inFlight.set(cacheKey, promise);
  return promise;
};

export const clearApiCache = () => {
  cache.clear();
  inFlight.clear();
};

export const invalidateUserConfig = () =>
  invalidateByPrefix("User/GetUserConfig:");

export const invalidateNotificationCount = () =>
  invalidateByPrefix("General/GetUnReadNotificationCount:");

export const resetNotificationCountForLogin = () => {
  try {
    localStorage.removeItem("NotificationCountFetched");
    localStorage.removeItem("NotificationCountData");
  } catch (e) {
    // ignore storage errors
  }
  invalidateNotificationCount();
};

const dispatchNotificationCountUpdated = (count) => {
  window.dispatchEvent(
    new CustomEvent("notificationCountUpdated", { detail: { count } })
  );
};

export const completeLoginNotificationFetch = async () => {
  resetNotificationCountForLogin();
  try {
    const count = await getUnreadNotificationCount({ forceRefresh: true });
    if (count != null) {
      dispatchNotificationCountUpdated(count);
    }
    return count;
  } catch (e) {
    return null;
  } finally {
    window.dispatchEvent(new Event("userLoggedIn"));
  }
};

export const invalidateMessageCount = () =>
  invalidateByPrefix("Message/AllUnreadMsgsCount:");

export const invalidateOwnCompaniesList = () =>
  invalidateByPrefix("Company/GetOwnCompaniesList:");

export const invalidateOwnInfo = () =>
  invalidateByPrefix("UserProfile/GetOwnInfo:");

export const invalidateConnectionList = () =>
  invalidateByPrefix("Connection/GetConnectionList:");

export const invalidateCheckConnection = (userUID) => {
  if (userUID) {
    const sessionId = getSessionId();
    const langKey = getLangKey();
    const cacheKey = buildCacheKey("Connection/CheckConnection", {
      sessionId,
      userUID,
      langKey,
    });
    cache.delete(cacheKey);
    inFlight.delete(cacheKey);
  } else {
    invalidateByPrefix("Connection/CheckConnection:");
  }
};

export const invalidateShortMutualInfo = () =>
  invalidateByPrefix("UserFollow/GetShortMutualUserInfo:");

export const getUserConfig = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("User/GetUserConfig", { sessionId, langKey });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("User/GetUserConfig", {
        deviceType: "Web",
        sessionId,
        langKey,
      });
      return res.data.responseData;
    },
    { ttl: TTL.USER_CONFIG, forceRefresh }
  );
};

export const getUnreadNotificationCount = async ({
  forceRefresh = false,
} = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("General/GetUnReadNotificationCount", {
    sessionId,
    langKey,
  });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("General/GetUnReadNotificationCount", {
        deviceType: "Web",
        sessionId,
        langKey,
      });
      const count = res.data.responseData;
      localStorage.setItem("NotificationCountData", count);
      return count;
    },
    { ttl: TTL.NOTIFICATION_COUNT, forceRefresh }
  );
};

export const getUnreadMessagesCount = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("Message/AllUnreadMsgsCount", {
    sessionId,
    langKey,
  });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("Message/AllUnreadMsgsCount", {
        deviceType: "Web",
        sessionId,
        langKey,
      });
      const count = res.data.responseData;
      localStorage.setItem("MsgCountData", count);
      return count;
    },
    { ttl: TTL.MESSAGE_COUNT, forceRefresh }
  );
};

export const getOwnCompaniesList = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("Company/GetOwnCompaniesList", {
    sessionId,
    langKey,
  });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("Company/GetOwnCompaniesList", {
        sessionId,
        deviceType: "Web",
        langKey,
      });
      return res.data.responseData;
    },
    { ttl: TTL.OWN_COMPANIES, forceRefresh }
  );
};

export const getEmojiDDL = async (langKey, { forceRefresh = false } = {}) => {
  const language = langKey ?? localStorage.getItem("Lang") ?? "en";
  const cacheKey = buildCacheKey("Master/GetEmojiDDL", { language });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.get(`Master/GetEmojiDDL?LangKey=${language}`);
      return res.data.responseData;
    },
    { ttl: TTL.EMOJI_DDL, forceRefresh }
  );
};

export const getConnectionList = async (
  payload,
  { forceRefresh = false } = {}
) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const fullPayload = {
    deviceType: "Web",
    sessionId,
    langKey,
    ...payload,
  };
  const cacheKey = buildCacheKey("Connection/GetConnectionList", fullPayload);

  return fetchWithDedup(
    cacheKey,
    async () => axios.post("Connection/GetConnectionList", fullPayload),
    { ttl: TTL.CONNECTION_LIST, forceRefresh }
  );
};

export const checkConnection = async (
  userUID,
  { forceRefresh = false } = {}
) => {
  const sessionId = getSessionId();
  if (!sessionId || !userUID) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("Connection/CheckConnection", {
    sessionId,
    userUID,
    langKey,
  });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("Connection/CheckConnection", {
        deviceType: "Web",
        sessionId,
        userUID,
        langKey,
      });
      return res.data;
    },
    { ttl: TTL.CHECK_CONNECTION, forceRefresh }
  );
};

export const getShortMutualUserInfo = async (
  userUid,
  { forceRefresh = false } = {}
) => {
  const sessionId = getSessionId();
  if (!sessionId || !userUid) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("UserFollow/GetShortMutualUserInfo", {
    sessionId,
    userUid,
    langKey,
  });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("UserFollow/GetShortMutualUserInfo", {
        deviceType: "Web",
        sessionId,
        userUid,
        langKey,
      });
      return res.data.responseData;
    },
    { ttl: TTL.SHORT_MUTUAL, forceRefresh }
  );
};

export const getRecommendationList = async (
  payload,
  { forceRefresh = false } = {}
) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const fullPayload = {
    deviceType: "Web",
    sessionId,
    langKey,
    ...payload,
  };
  const cacheKey = buildCacheKey(
    "UserFollow/GetRecommendationList",
    fullPayload
  );

  return fetchWithDedup(
    cacheKey,
    async () => axios.post("UserFollow/GetRecommendationList", fullPayload),
    { ttl: TTL.RECOMMENDATION_LIST, forceRefresh }
  );
};

export const getOwnInfo = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("UserProfile/GetOwnInfo", { sessionId, langKey });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("/UserProfile/GetOwnInfo", {
        sessionId,
        deviceType: "Web",
        langKey,
      });
      return res.data.responseData;
    },
    { ttl: TTL.OWN_INFO, forceRefresh }
  );
};

export const getProfilePercentage = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const cacheKey = buildCacheKey("UserProfile/GetProfilePercentage", { sessionId });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("UserProfile/GetProfilePercentage", {
        deviceType: "Web",
        sessionId,
      });
      return res.data.responseData;
    },
    { ttl: TTL.PROFILE_PERCENTAGE, forceRefresh }
  );
};

export const syncAccountAlgoData = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("Feature/SyncAccountAlgoData", {
    sessionId,
    langKey,
  });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("Feature/SyncAccountAlgoData", {
        deviceType: "Web",
        sessionId,
        langKey,
      });
      return res.data.responseData;
    },
    { ttl: TTL.SYNC_ACCOUNT, forceRefresh }
  );
};
